import { sanitizeHTML } from '@sunwebgroup/uds-ui/utils'
import { mqMin, space, tok, tokPx, zIndex } from '@sunwebgroup/ui-components'
import { focusOutline, Icon, Sheet, Skeleton, Typography, useBreakpoints } from '@sunwebgroup/ui-components/uds'
import { styled } from 'styled-components'
import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import getUserRatingLabelIntlId from '@/scopes/common/components/utils/getUserRatingLabelIntlId'
import { useUserReviewsInitialData } from '@/scopes/products/user-reviews/hooks'
import { ReviewDetails } from '../UserReviews/ReviewDetails/ReviewDetails'

const RatingScoreSkeleton = styled(Skeleton)`
  width: 98px;
  height: 24px;
  border-radius: ${tokPx('primitives_corner_radius_l')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: 64px;
    width: 64px;
  }
`

const LabelSkeleton = styled(Skeleton)`
  width: 180px;
  height: ${tokPx('components_tag_m_height')};
  border-radius: ${tokPx('primitives_corner_radius_l')};

  &:last-child {
    display: none;
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: 20px;
    &:last-child {
      display: block
    }
  }
`

const LabelWrapperSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_8')};
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_8')};
  position: relative;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    gap: ${space('primitives_space_16')};
  }
`

const WrapperSkeleton = styled(Wrapper)`
  flex-direction: row;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    flex-direction: row-reverse;
  }
`

const AmountWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${space('primitives_space_4')};
`

const ButtonAmount = styled.button`
  display: flex;
  align-items: center;
  color: ${tok('semantics_content_secondary_color')};
  z-index: ${zIndex.level2};
  padding: 4px;
  margin-left: -4px;
  margin-right: -4px;

  ${focusOutline(-4)}
`

const Amount = styled(Typography)`
  ${Typography.modVariant('body-s-regular-tight')};
  color: ${tok('semantics_content_secondary_color')};
  white-space: nowrap;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Typography.modVariant('body-m-regular-tight')};
  }
`

const RatingValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
`

const RatingBadge = styled(Typography)`
  ${Typography.modVariant('display-g-regular')};
  background-color: ${tok('components_rating_background_color')};
  color: ${tok('components_rating_color')};
  border-radius: ${tokPx('primitives_corner_radius_l')};
  padding: ${space('primitives_space_16')} ${space('primitives_space_12')};
  display: block;
  min-width: ${space('primitives_space_64')};
  text-align: center;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Typography.modVariant('display-g-regular')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Typography.modVariant('display-f-regular')};
  }
`

const RatingLabel = styled(Typography)`
  ${Typography.modVariant('display-g-regular')};
  color: ${tok('components_rating_color')};
  display: flex;
  align-items: center;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Typography.modVariant('display-g-regular')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Typography.modVariant('display-f-regular')};
  }
`

const OverlayButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: ${tokPx('primitives_corner_radius_l')};
  z-index: ${zIndex.level1};

  ${focusOutline()}
`

const Rating = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space('primitives_space_2')};
  padding-top: ${tokPx('components_tag_m_padding_top')};
  padding-right: ${tokPx('components_tag_m_padding_right')};
  padding-bottom: ${tokPx('components_tag_m_padding_bottom')};
  padding-left: ${tokPx('components_tag_m_padding_left')};
  border-radius: ${tokPx('components_tag_m_corner_radius')};
  background-color: ${tok('semantics_views_tinted_background_color')};
  color: ${tok('semantics_content_color')};

  ${Typography.modVariant('body-s-bold-flat')}
`

export function StandaloneReviewsScore() {
  const ratingsCount = useCMSPageField('userReviews.ratingsCount')
  const ratingAverage = useCMSPageField('userReviews.ratingAverage')
  const { reviewsAmount, isFetched, isLoading } = useUserReviewsInitialData()
  const intl = useIntl()
  const { isMobile } = useBreakpoints()

  const reviewScoreBarLabel = intl.formatMessage({ id: getUserRatingLabelIntlId(ratingAverage) })
  const displayedReviewsAmount = reviewsAmount || ratingsCount

  if (!ratingAverage)
    return null

  if (isLoading) {
    return (
      <WrapperSkeleton>
        <RatingScoreSkeleton />
        <LabelWrapperSkeleton>
          <LabelSkeleton />
          <LabelSkeleton />
        </LabelWrapperSkeleton>
      </WrapperSkeleton>
    )
  }

  const renderReviewsAmountSection = () => (
    <AmountWrapper>
      <Amount><u>{intl.formatMessage({ id: 'reviews_score_amount_label' }, { amount: displayedReviewsAmount })}</u></Amount>
      <Sheet.Root>
        <Sheet.Trigger>
          <ButtonAmount aria-label={intl.formatMessage({ id: 'reviews_legal_information' })}>
            <Icon glyph="InformationInCircle" size={16} />
          </ButtonAmount>
        </Sheet.Trigger>
        <Sheet.Body side="right">
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'reviews_legal_disclaimer_title' })}
          />
          <Sheet.Content>
            <Typography
              // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(intl.formatMessage({ id: 'reviews_legal_disclaimer_html' })) }}
            />
          </Sheet.Content>
        </Sheet.Body>
      </Sheet.Root>
    </AmountWrapper>
  )

  if (!displayedReviewsAmount || (isFetched && reviewsAmount < 3))
    return null

  return (
    <Wrapper>
      <ReviewDetails>
        <OverlayButton aria-label={intl.formatMessage({ id: 'reviews_view_all' })} />
      </ReviewDetails>
      {isMobile
        ? (
            <>
              {ratingAverage && (
                <Rating>
                  {`${ratingAverage} ${reviewScoreBarLabel}`}
                </Rating>
              )}
              {renderReviewsAmountSection()}
            </>
          )
        : (
            <>
              <RatingValue>
                <RatingLabel>
                  {reviewScoreBarLabel}
                </RatingLabel>
                {renderReviewsAmountSection()}
              </RatingValue>
              {ratingAverage && <RatingBadge>{ratingAverage}</RatingBadge>}
            </>
          )}
    </Wrapper>
  )
}
